import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

//services
import { TranslationHandlerService } from './shared/localization/translation-handler.service';
import { UtilService } from './shared/util/util.service';
import { AuthService } from './shared/auth/auth.service';
import { LogService } from './shared/log/log.service';
import { AuthUserInfo } from './shared/auth/auth-user-info.model';
import { LocalStorageService } from './shared/storage/local-storage.service';
import { PlatformService } from './shared/platform/platform.service';
import { CacheService } from './shared/cache/cache.service';
import { UserService } from './users/shared/user.service';
import { UserRead } from './users/shared/user-read.model';
import { ConfigService } from './config/shared/config.service';
import { AppContext } from './config/shared/app-context.model';

//container components
import { MainContainerComponent } from './shared/container/main-container.component';

// declared in index.html
declare let pendo: any; // pendo tracking
declare let FS: any; // FullStory tracking

@Component({
  selector: 'wl-app',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  private _translate: TranslateService;
  private _router: Router;
  private _authService: AuthService;
  private _utilService: UtilService;
  private _localStorageService: LocalStorageService;
  private _platformService: PlatformService;
  private _cacheService: CacheService;
  private _logService: LogService;

  public title: string;

  public constructor(
    translate: TranslateService,
    router: Router,
    authService: AuthService,
    utilService: UtilService,
    localStorageService: LocalStorageService,
    platformService: PlatformService,
    cacheService: CacheService,
    private _userService: UserService,
    private _configService: ConfigService,
    logService: LogService
  ) {
    this._translate = translate;
    this._router = router;
    this._authService = authService;
    this._utilService = utilService;
    this._localStorageService = localStorageService;
    this._platformService = platformService;
    this._cacheService = cacheService;
    this._logService = logService;

    this.title = 'atlas-spa';

    //cacheService here
    this._cacheService.updateApplicationCodeCacheIfNecessary();

    let userLang: string = '';
    if (navigator != undefined) {
      if (navigator.language != undefined) {
        let langCulture: string[] = navigator.language.split('-');
        if (langCulture.length > 0) {
          userLang = langCulture[0]; // use navigator lang if available
        }
      }
    }

    //for testing force spanish
    //userLang = 'es';

    if (userLang == undefined || userLang == '') {
      userLang = 'en';
    }

    // this language will be used as a fallback when a translation isn't found in the current language
    this._translate.setDefaultLang('en');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this._translate.use(userLang);

    //get is async while instant is sync
    //we can't be sure the translations have loaded here so we must
    //subscribe to the asycn observable
    this._translate.get('app.title').subscribe((value: string) => {
      this.title = value;
    });
  }

  ngOnInit() {
    if (!this._authService.currentUserIsAuthenticated()) {
      //send to the unauthenticated route
      this._logService.log('app.component load', 'application requires authentication');

      // const useSharedIdp = this._localStorageService.getBoolean('use-psfy-idp');
      // if (useSharedIdp) {
      //   this._router.navigate(['unauthenticated-psfy']);
      // } else {
      this._router.navigate(['unauthenticated']);
      // }
    } else {
      this.registerCurrentUserWithPendo();

      let newUrl = window.location.pathname;
      if (this._utilService.toLowerTrim(newUrl).indexOf('/unauthenticated') > -1 || this._utilService.toLowerTrim(newUrl) == '/') {
        this._router.navigateByUrl('dashboard');
      } else {
        //removing the router.navigate call as it strips out the QS params and actually isn't necessary
      }
    }
  }

  private addZendeskEmbeds(user: UserRead) {
    const zendeskEmbed = document.createElement('script');
    zendeskEmbed.setAttribute('src', `https://static.zdassets.com/ekr/snippet.js?key=a3685662-50fa-46f3-9d46-9c03a5cc64cf`);
    zendeskEmbed.id = 'ze-snippet';
    zendeskEmbed.async = false;
    document.documentElement.firstChild.appendChild(zendeskEmbed);
    zendeskEmbed.onload = () => {
      this.autoFillZenDesk(user);
    };
  }

  private autoFillZenDesk(user: UserRead) {
    const zendeskEmbed2 = document.createElement('script');
    zendeskEmbed2.innerText = `zE('webWidget', 'identify', {
        name: '${user.FullName}',
        email: '${user.Email}'
      });
      zE('webWidget', 'prefill', {
        name: '${user.FullName}',
        email: '${user.Email}'
      });`;
    zendeskEmbed2.async = false;
    document.documentElement.lastChild.appendChild(zendeskEmbed2);
  }
  /**
   * after user is logged in, or if the user refreshes and is already logged in
   * register this user as a visitor for pendo tracking
   */
  private registerCurrentUserWithPendo(): void {
    let promises: Promise<any>[] = [];

    let appContext: AppContext = null;
    let user: UserRead = null;
    let authData: AuthUserInfo = null;

    promises.push(this._configService.getAppContext().then((a: AppContext) => (appContext = a)));
    promises.push(this._authService.getUserInfo().then((x) => (authData = x)));
    promises.push(this._userService.getCurrentUser().then((u: UserRead) => (user = u)));

    Promise.all(promises).then(() => {
      if (!user || !authData || !appContext) return;

      let userUniqueId: string = this._authService.getTenant() + '-' + user.UserNum;
      let associationVerticalType: string = authData.wli_associnfo_associationverticaltype
        ? authData.wli_associnfo_associationverticaltype
        : authData['wli_associnfo_ associationverticaltype']
        ? authData['wli_associnfo_ associationverticaltype']
        : '';

      if (pendo) {
        // Call this whenever information about your visitors becomes available
        // Please use Strings, Numbers, or Bools for value types.
        pendo.initialize({
          visitor: {
            id: userUniqueId, // Required if user is logged in
            email: user.Email, // Optional
            role: user.Role, // Optional

            // You can add any additional visitor level key-values here,
            // as long as it's not one of the above reserved names.
            UserName: user.UserName,
            FullName: user.FullName,
          },

          account: {
            id: appContext.CorpProfileId, // Highly recommended
            name: authData.wli_associnfo_orgname, // Optional
            // planLevel:    // Optional
            // planPrice:    // Optional
            // creationDate: // Optional

            // You can add any additional account level key-values here,
            // as long as it's not one of the above reserved names.
            associationverticaltype: associationVerticalType,
          },
        });
      }

      if (FS) {
        // FullStory
        FS.identify(userUniqueId, {
          displayName: user.FullName,
          email: user.Email,

          // custom variables
          // http://help.fullstory.com/develop-js/setuservars.
          userName_str: user.UserName,
          associationCorpProfileId_int: appContext.CorpProfileId,
          associationName_str: authData.wli_associnfo_orgname,
          associationVerticalType_str: associationVerticalType,
        });
      }

      console.log('appending zendesk widget');
      this.addZendeskEmbeds(user);
    });
  }
}
